import Link from 'next/link';
import cn from 'classnames';
import dayjs from 'dayjs';

import { Box, Card } from 'react-scale/components';

import s from './box.module.css';

export const ResourceCard = ({ content, featured = false }) => {
  const strippedString = content.description.replace(/(<([^>]+)>)/gi, '');
  const Resource = () => (
    <Card
      className={cn(
        s.card,
        'bg-codGray rounded-lg transition duration-300 ease-in-out shadow-md hover:shadow-xl'
      )}
      img={{
        src: content.image,
        alt: `${content.title} card image`,
        height: featured ? 217 : 160
      }}
      overtitle={{
        className: 'text-sm text-scale-mauve mb-4',
        text: content.subtitle
      }}
      title={content.title.replace(/([:])+/g, '')}
      body={featured && <p className={s.body}>{strippedString}</p>}
    >
      <div className="flex flex-wrap -mt-2 p-6 pt-0 max-w-full flex-[0]">
        <span className="px-2 py-1 mt-2 mr-2 text-sm bg-black rounded-md text-scale-skyblue max-w-max min-w-max">
          Solution Brief / White Paper
        </span>
      </div>
    </Card>
  );

  return (
    (<Link
      href={`/resources/download/${content.slug}`}
      className="flex flex-col"
      target="_blank">

      {featured ? (
        <Box type="gradient">
          <Resource />
        </Box>
      ) : (
        <Resource />
      )}

    </Link>)
  );
};

export const BlogCard = ({ content, featured = false }) => {
  const strippedString = content.extract?.replace(/(<([^>]+)>)/gi, '');
  const Blog = () => (
    <Card
      className={cn(
        s.card,
        'bg-codGray rounded-lg transition duration-300 ease-in-out shadow-md hover:shadow-xl'
      )}
      img={{
        src: content.coverImage ?? '/static/global/scale.png',
        alt: content.title,
        height: featured ? 217 : 160
      }}
      overtitle={{
        className: 'text-sm text-pink tracking-widest mb-4',
        text: content.category?.value.data.name
      }}
      title={content.title}
      body={featured && <p className={s.body}>{strippedString}</p>}
    >
      <div className="flex flex-wrap -mt-2 p-6 pt-0 max-w-full flex-[0]">
        <span className="px-2 py-1 mt-2 mr-2 text-sm bg-black rounded-md text-scale-skyblue max-w-max min-w-max">
          Blog
        </span>
      </div>
    </Card>
  );

  return (
    (<Link href={`/blog/${content.slug}`} className="flex flex-col" target="_blank">

      {featured ? (
        <Box type="gradient">
          <Blog />
        </Box>
      ) : (
        <Blog />
      )}

    </Link>)
  );
};

export const EventCard = ({ content, featured = false }) => {
  const date = dayjs(content.date).format('MMM Do YYYY');

  let banner;

  if (content?.bannerInput) {
    banner = content.bannerInput;
  } else if (content?.banner) {
    banner = content.banner;
  } else banner = '/static/global/scale.png';

  const Event = () => (
    <Card
      className={cn(
        s.card,
        'bg-codGray rounded-lg transition duration-300 ease-in-out shadow-md hover:shadow-xl'
      )}
      img={{
        src: banner,
        alt: content?.title ?? 'scale',
        height: featured ? 217 : 160
      }}
      overtitle={{
        className: 'text-sm text-scale-lightskyblue mb-4',
        text: date
      }}
      title={content.title}
      body={featured && <p className={s.body}>{content.metaDescription}</p>}
    >
      <div className="flex flex-wrap -mt-2 p-6 pt-0 max-w-full flex-[0]">
        <span className="px-2 py-1 mt-2 mr-2 text-sm bg-black rounded-md text-scale-skyblue max-w-max min-w-max">
          Event
        </span>
        <span className="px-2 py-1 mt-2 mr-2 text-sm bg-black rounded-md text-scale-skyblue max-w-max min-w-max">
          {content.category}
        </span>
      </div>
    </Card>
  );

  return (
    (<Link
      href={content.link ?? `events/${content.slug}`}
      className="flex flex-col"
      target="_blank">

      {featured ? (
        <Box type="gradient">
          <Event />
        </Box>
      ) : (
        <Event />
      )}

    </Link>)
  );
};

export const CustomerCard = ({ content, featured = false }) => {
  const Customer = () => (
    <Card
      className={cn(
        s.card,
        'bg-codGray rounded-lg transition duration-300 ease-in-out shadow-md hover:shadow-xl'
      )}
      img={{
        src: content.coverImage ?? '/static/global/scale.png',
        alt: `${content.title} card`,
        height: featured ? 217 : 160
      }}
      overtitle={{
        className: 'text-sm text-scale-lightskyblue mb-4',
        text: content.title
      }}
      title={content.description}
      body={featured && <p className={s.body}>{content.metaDescription}</p>}
    >
      <div className="flex flex-wrap -mt-2 p-6 pt-0 max-w-full flex-[0]">
        <span className="px-2 py-1 mt-2 mr-2 text-sm bg-black rounded-md text-scale-skyblue max-w-max min-w-max">
          Case Study
        </span>
      </div>
    </Card>
  );

  return (
    (<Link
      href={content.slug ? content.slug : content.url}
      className="flex flex-col"
      target="_blank">

      {featured ? (
        <Box type="gradient">
          <Customer />
        </Box>
      ) : (
        <Customer />
      )}

    </Link>)
  );
};

export const DatasetCard = ({ content, featured = false }) => {
  const date = dayjs(content.date).format('MMM Do YYYY');
  const Dataset = () => (
    <Card
      className={cn(
        s.card,
        'bg-codGray rounded-lg transition duration-300 ease-in-out shadow-md hover:shadow-xl'
      )}
      img={{
        src: content?.logoFront ?? '/static/global/scale.png',
        alt: content?.title ?? 'scale',
        height: featured ? 217 : 160
      }}
      overtitle={{
        className: 'text-sm text-scale-lightskyblue mb-4',
        text: date
      }}
      title={content.title}
      body={featured && <p className={s.body}>{content.metaDescription}</p>}
    >
      <div className="flex flex-wrap -mt-2 p-6 pt-0 max-w-full flex-[0]">
        <span className="px-2 py-1 mt-2 mr-2 text-sm bg-black rounded-md text-scale-skyblue max-w-max min-w-max">
          Open Dataset
        </span>
      </div>
    </Card>
  );

  return (
    (<Link
      href={content?.link?.includes('http') ? content.link : content.href}
      className="flex flex-col"
      target="_blank">

      {featured ? (
        <Box type="gradient">
          <Dataset />
        </Box>
      ) : (
        <Dataset />
      )}

    </Link>)
  );
};

export const InterviewCard = ({ content, featured = false }) => {
  const Interview = () => (
    <Card
      className={cn(
        s.card,
        'bg-codGray rounded-lg transition duration-300 ease-in-out shadow-md hover:shadow-xl'
      )}
      img={{
        src: content.image,
        alt: content.title,
        height: featured ? 217 : 160
      }}
      overtitle={{
        className: 'text-sm text-scale-lightskyblue mb-4',
        text: content.subtitle
      }}
      title={content.title}
      body={featured && <p className={s.body}>{content.description}</p>}
    >
      <div className="flex flex-wrap -mt-2 p-6 pt-0 max-w-full flex-[0]">
        <span className="px-2 py-1 mt-2 mr-2 text-sm bg-black rounded-md text-scale-skyblue max-w-max min-w-max">
          Interview
        </span>
      </div>
    </Card>
  );

  return (
    (<Link
      href={`/interviews/${content.slug}`}
      className="flex flex-col"
      target="_blank">

      {featured ? (
        <Box type="gradient">
          <Interview />
        </Box>
      ) : (
        <Interview />
      )}

    </Link>)
  );
};
